import axios from "axios";
import { getToken } from "../utils/authStorage";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5010/api";

// ─── Interfaces ──────────────────────────────────────────────────────────

export interface FilterCondition {
  field: string;
  operator: "=" | "!=" | ">" | ">=" | "<" | "<=" | "between";
  value: string | number | boolean | [number, number];
}

export interface AdvancedFollowUpFilters {
  logic?: "AND" | "OR";
  conditions?: FilterCondition[];
}

export interface AdvancedFollowUpBooking {
  bookingId: string;
  bookingDate: string;
  customerName: string;
  customerContact: string;
  projectName: string;
  unitRef: string;
  unitType: string;
  wing: string;
  paymentTerms: string;
  saleDeedAmount: number;
  receivableAmount: number;
  totalAmount: number;
  saleDeedReceived: number;
  receivableReceived: number;
  totalReceived: number;
  totalPending: number;
  receivedPercent: number;
  pendingPercent: number;
  paymentCount: number;
  lastPaymentDate: string | null;
  lastPaymentAmount: number | null;
  daysSinceBooking: number;
  daysSinceLastPayment: number | null;
  receivedInPeriod: number;
  status: string;
}

export interface AdvancedKPIs {
  totalBookings: number;
  totalSaleDeed: number;
  totalReceivable: number;
  totalExpected: number;
  totalReceived: number;
  totalPending: number;
  averageReceivedPercent: number;
  averagePendingPercent: number;
  highRiskCount: number;
  receivedInPeriod: number;
}

export interface Project {
  id: number;
  name: string;
}

export interface AdvancedFollowUpReportData {
  kpis: AdvancedKPIs;
  bookings: AdvancedFollowUpBooking[];
  projects: Project[];
}

// ─── Column Config ───────────────────────────────────────────────────────

export interface ColumnConfig {
  key: string;
  label: string;
  type: "number" | "text" | "currency" | "percent" | "date";
  visible: boolean;
  order: number;
}

// ─── Preset ──────────────────────────────────────────────────────────────

export interface ReportPreset {
  id: string;
  name: string;
  createdAt: string;
  filters: {
    project: string;
    bookingDateFrom: string;
    bookingDateTo: string;
    paymentDateFrom: string;
    paymentDateTo: string;
    conditions: FilterCondition[];
    logic: "AND" | "OR";
  };
  columns: ColumnConfig[];
}

// ─── Default Columns ─────────────────────────────────────────────────────

export const ALL_COLUMNS: ColumnConfig[] = [
  { key: "srNo", label: "Sr. No", type: "number", visible: true, order: 0 },
  { key: "wing", label: "Wing", type: "text", visible: true, order: 1 },
  { key: "unitRef", label: "Unit", type: "text", visible: true, order: 2 },
  { key: "bookingId", label: "Booking ID", type: "text", visible: false, order: 3 },
  { key: "bookingDate", label: "Booking Date", type: "date", visible: true, order: 4 },
  { key: "customerName", label: "Customer Name", type: "text", visible: true, order: 5 },
  { key: "customerContact", label: "Contact", type: "text", visible: false, order: 6 },
  { key: "projectName", label: "Project", type: "text", visible: true, order: 7 },
  { key: "unitType", label: "Unit Type", type: "text", visible: false, order: 8 },
  { key: "paymentTerms", label: "Payment Terms", type: "text", visible: true, order: 9 },
  { key: "saleDeedAmount", label: "Sale Deed Amount", type: "currency", visible: false, order: 10 },
  { key: "receivableAmount", label: "Receivable Amount", type: "currency", visible: false, order: 11 },
  { key: "totalAmount", label: "Total Amount", type: "currency", visible: true, order: 12 },
  { key: "saleDeedReceived", label: "Sale Deed Received", type: "currency", visible: false, order: 13 },
  { key: "receivableReceived", label: "Receivable Received", type: "currency", visible: false, order: 14 },
  { key: "totalReceived", label: "Total Received", type: "currency", visible: true, order: 15 },
  { key: "totalPending", label: "Total Pending", type: "currency", visible: true, order: 16 },
  { key: "receivedPercent", label: "Received %", type: "percent", visible: true, order: 17 },
  { key: "pendingPercent", label: "Pending %", type: "percent", visible: true, order: 18 },
  { key: "paymentCount", label: "Payment Count", type: "number", visible: false, order: 19 },
  { key: "lastPaymentDate", label: "Last Payment Date", type: "date", visible: false, order: 20 },
  { key: "lastPaymentAmount", label: "Last Payment Amount", type: "currency", visible: false, order: 21 },
  { key: "daysSinceBooking", label: "Days Since Booking", type: "number", visible: false, order: 22 },
  { key: "daysSinceLastPayment", label: "Days Since Last Payment", type: "number", visible: true, order: 23 },
  { key: "receivedInPeriod", label: "Received in Period", type: "currency", visible: true, order: 24 },
  { key: "status", label: "Status", type: "text", visible: true, order: 25 },
];

// ─── Local Storage Keys ──────────────────────────────────────────────────

const PRESETS_KEY = "advancedFollowUpPresets";
const COLUMNS_KEY = "advancedFollowUpColumns";

// ─── Service ─────────────────────────────────────────────────────────────

class AdvancedFollowUpReportService {
  private getAuthHeader() {
    const token = getToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  // ─── API Calls ──────────────────────────────────────────────────

  async getReportData(
    projectId?: string,
    bookingDateFrom?: string,
    bookingDateTo?: string,
    paymentDateFrom?: string,
    paymentDateTo?: string,
    filters?: AdvancedFollowUpFilters
  ): Promise<AdvancedFollowUpReportData> {
    const payload: any = {};
    if (projectId) payload.projectId = projectId;
    if (bookingDateFrom) payload.bookingDateFrom = bookingDateFrom;
    if (bookingDateTo) payload.bookingDateTo = bookingDateTo;
    if (paymentDateFrom) payload.paymentDateFrom = paymentDateFrom;
    if (paymentDateTo) payload.paymentDateTo = paymentDateTo;
    if (filters) payload.filters = filters;

    const response = await axios.post(
      `${API_URL}/reports/advanced-follow-up/data`,
      payload,
      this.getAuthHeader()
    );
    return response.data;
  }

  // ─── Presets (localStorage) ─────────────────────────────────────

  getPresets(): ReportPreset[] {
    try {
      const raw = localStorage.getItem(PRESETS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  savePreset(preset: ReportPreset): void {
    const presets = this.getPresets();
    // Update existing or add new
    const idx = presets.findIndex((p) => p.id === preset.id);
    if (idx >= 0) {
      presets[idx] = preset;
    } else {
      presets.push(preset);
    }
    localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
  }

  deletePreset(id: string): void {
    const presets = this.getPresets().filter((p) => p.id !== id);
    localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
  }

  // ─── Column Config (localStorage) ──────────────────────────────

  getColumnConfig(): ColumnConfig[] {
    try {
      const raw = localStorage.getItem(COLUMNS_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as ColumnConfig[];
        // Merge with latest ALL_COLUMNS to pick up any new columns
        const savedKeys = new Set(saved.map((c) => c.key));
        const merged = [...saved];
        for (const col of ALL_COLUMNS) {
          if (!savedKeys.has(col.key)) {
            merged.push({ ...col, order: merged.length });
          }
        }
        return merged.sort((a, b) => a.order - b.order);
      }
    } catch {
      // ignore
    }
    return [...ALL_COLUMNS];
  }

  saveColumnConfig(columns: ColumnConfig[]): void {
    localStorage.setItem(COLUMNS_KEY, JSON.stringify(columns));
  }
}

export default new AdvancedFollowUpReportService();
